import { useEffect } from "react";

import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import MapHandlerMap from "@app/types/map/MapHandlerMap";
import { isPinEvent } from "@app/util/map/pinEventClaim";

import doubleClickDetector from "./doubleClickDetector";

export interface MapEventHandlerOptions {
  // By default events that land on a pin are skipped; set to receive them too.
  includePinEvents?: boolean;
}

const CLAIMABLE_EVENTS = new Set<string>([
  "click",
  "contextmenu",
  "dblclick",
  "mousedown",
  "mousemove",
  "mouseout",
  "mouseover",
  "mouseup",
  "rightclick",
]);

const useMapEventHandler = <M extends google.maps.Map, N extends keyof MapHandlerMap<M>>(
  map: M | undefined,
  eventName: N,
  handler: MapHandlerMap<M>[N],
  options?: MapEventHandlerOptions
) => {
  const internalHandler = useDynamicCallback(function (this: M, ...args: any) {
    // Taken at event time: a click is delayed, and the pin may change before it runs.
    const claimed =
      !!map &&
      CLAIMABLE_EVENTS.has(eventName as string) &&
      isPinEvent(map, args[0] as google.maps.MapMouseEvent);
    const skip = claimed && !options?.includePinEvents;
    if (eventName === "click") {
      // Stop event from default handling.
      // We need this because with doubleClickDetector there's a timeout between event
      // and when we start processing it. Hence, in some cases google maps can handle
      // this event themselves, e.g. POI clicks, which is unwanted.
      (args[0] as google.maps.MapMouseEvent).stop();
      if (!skip) {
        doubleClickDetector.handleSingleClick(() => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
          (handler as Function).apply(this, args);
        });
      }
      return;
    } else if (eventName === "dblclick") {
      doubleClickDetector.handleDoubleClick();
    }
    if (skip) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    (handler as Function).apply(this, args);
  });

  useEffect(() => {
    if (map) {
      const listener = map.addListener(eventName, internalHandler);
      return () => {
        listener.remove();
      };
    }
  }, [eventName, internalHandler, map]);
};

export default useMapEventHandler;
